/**
 * Lettermint credentials form — the registry's two secret fields
 * (projectToken, teamToken) on one screen, mounted via show() as an
 * overlay. Both live on one screen because the registry declares exactly two
 * fields; a FIRST save still submits both together, since neither has a
 * stored value to fall back to. Once a credential is stored, each field is
 * independently optional — a blank one omits its KEY from the dispatched
 * payload, which is the save contract's "keep the stored value" signal
 * (packages/types/src/saveIntegration.action.ts), so rotating the team token
 * never forces re-pasting the project token nothing shows back. An empty
 * string is not that signal: every declared field is `.min(1)`.
 * Replacing a stored token routes through a confirm() naming the token that
 * is actually travelling — project, team, or both — because a team-token
 * rotation leaves outbound mail on the kept project token, which a
 * whole-credential warning would contradict.
 * Pasting one token where the other belongs is the single likeliest
 * mistake, so each field validates its own prefix inline
 * (lettermint.state.ts) rather than waiting for the round trip.
 *
 * Below the tokens, a delivery-route picker: Lettermint's webhook create
 * requires a route id, and routes are discoverable only against an
 * already-saved team token (listLettermintRoutes.action.ts) — so first-time
 * setup shows an explanatory note instead of a picker (there is nothing to
 * fetch yet), and only a re-save (hasStoredCredential) can ever offer one.
 * With exactly one route there is no real choice, so it auto-selects and the
 * picker renders nothing at all.
 *
 * Mountable and zero-I/O: every fact this form needs to decide anything —
 * whether a credential is already stored for this id
 * (hasStoredCredential), whether a save is in flight (saving), how a failed save's
 * classified reason routes (teamTokenError / footerNotice), whether the
 * in-flight save just resolved ok:true (succeeded), and the route picker's
 * own data (routes / routesPhase / storedRouteId) — arrives as a property
 * from the card that mounts it
 * (web/src/admin/settings/integrations/lettermint/lettermint-integration.view.ts),
 * which alone has access to the live saveIntegrationAction$/
 * integrationSavePhases/integrationsStatus cells and the route-fetch
 * channel. This form dispatches its intent as a `save` CustomEvent and a
 * route (re)fetch as a `load-routes` CustomEvent, and closes itself — via
 * the platform close contract (`CustomEvent('close')`) — either on the
 * operator's own click or the instant `succeeded` flips true.
 */
import { overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { confirm } from "@mhmo91/schmancy/overlay";
import type { ListLettermintRoutes } from "@momo/types";
import { html, type PropertyValues, type TemplateResult } from "lit";
import { property, state } from "lit/decorators.js";
import { choose } from "lit/directives/choose.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../../i18n/localize.ts";
import {
	projectTokenPrefixError,
	teamTokenPrefixError,
} from "./lettermint.state.ts";

// Lettermint's own API-token guide — covers project vs team tokens, which is
// exactly the distinction this form's two fields turn on.
const LETTERMINT_DOCS_URL =
	"https://lettermint.co/docs/api-reference/introduction";

@schmancyElement("lettermint-credentials-form")
export class LettermintCredentialsForm extends LocalizeMixin(SchmancyElement) {
	/** True only once a credential has previously been proven and stored —
	 *  what makes each token field independently optional (blank = keep the
	 *  stored one), what gates the confirm() step when a token IS pasted, and
	 *  what makes the route picker fetchable at all; first-time setup has
	 *  none of the three. */
	@property({ type: Boolean }) hasStoredCredential = false;
	/** Whether the last dispatched save is still in flight. */
	@property({ type: Boolean }) saving = false;
	/** Flips true the instant the in-flight save resolves ok:true — the
	 *  signal this form closes itself on. */
	@property({ type: Boolean }) succeeded = false;
	/** The save-time reason-routed message for the team token specifically
	 *  (invalid / notAuthorized) — null while nothing has failed that way. */
	@property({ attribute: false }) teamTokenError: string | null = null;
	/** The save-time reason-routed message that indicts neither field
	 *  (unreachable, or a transport/authorization failure) — null while
	 *  nothing has failed that way. */
	@property({ attribute: false }) footerNotice: string | null = null;
	/** The routes this workspace's Lettermint account currently offers —
	 *  empty until a fetch resolves. Meaningful only once
	 *  hasStoredCredential is true; the card never fetches before then. */
	@property({ attribute: false })
	routes: readonly ListLettermintRoutes.LettermintRoute[] = [];
	/** This mount's own route-fetch status — the card triggers the fetch
	 *  itself; "idle" before any fetch, "loading" while in flight, "loaded"
	 *  once resolved (routes may still be empty), "error" on a failed
	 *  fetch. */
	@property({ type: String }) routesPhase:
		| "idle"
		| "loading"
		| "loaded"
		| "error" = "idle";
	/** The route already configured on a prior save, if any — preselects the
	 *  picker so re-saving doesn't silently drop the existing choice. */
	@property({ attribute: false }) storedRouteId: string | null = null;

	@state() private projectToken = "";
	@state() private teamToken = "";
	@state() private projectTokenTouched = false;
	@state() private teamTokenTouched = false;
	@state() private projectTokenRevealed = false;
	@state() private teamTokenRevealed = false;
	@state() private routeId: string | null = null;
	@state() private routeIdSeeded = false;

	// Seeding routeId here — before render(), in the same update cycle — is
	// what makes the preselection visible in the picker's first render: doing
	// this in updated() instead sets the property AFTER render already ran,
	// which only schedules a second update cycle for the value to show up in.
	protected override willUpdate(changed: PropertyValues<this>): void {
		super.willUpdate(changed);
		if (changed.has("saving") && !this.saving) this.admitted = false;
		if (
			!this.routeIdSeeded &&
			this.routesPhase === "loaded" &&
			this.routes.length > 0
		) {
			this.routeIdSeeded = true;
			this.routeId =
				this.routes.length === 1
					? (this.routes[0]?.id ?? null)
					: this.storedRouteId;
		}
	}

	protected override updated(changed: PropertyValues<this>): void {
		super.updated(changed);
		if (changed.has("succeeded") && this.succeeded) this.close();
	}

	private close(): void {
		this.dispatchEvent(
			new CustomEvent("close", { bubbles: true, composed: true }),
		);
	}

	@state() private confirming = false;
	@state() private admitted = false;
	@state() private actionHeight: number | null = null;

	private async save(): Promise<void> {
		if (this.confirming || this.admitted) return;
		this.projectTokenTouched = true;
		this.teamTokenTouched = true;
		const projectToken = this.projectToken.trim();
		const teamToken = this.teamToken.trim();
		const valid =
			(this.hasStoredCredential ||
				(projectToken.length > 0 && teamToken.length > 0)) &&
			!projectTokenPrefixError(projectToken) &&
			!teamTokenPrefixError(teamToken);
		if (!valid || this.saving) return;
		this.actionHeight =
			this.shadowRoot?.firstElementChild?.getBoundingClientRect().height ??
			null;
		if (
			this.hasStoredCredential &&
			(projectToken.length > 0 || teamToken.length > 0)
		) {
			// A confirm names the token actually travelling: on a team-token
			// rotation the project token is kept, so outbound mail is untouched
			// and a whole-credential warning would say otherwise.
			const replacing =
				projectToken.length > 0 && teamToken.length > 0
					? "both"
					: projectToken.length > 0
						? "projectToken"
						: "teamToken";
			this.confirming = true;
			const ok = await confirm({
				title: this.t(
					`admin.integrations.lettermint.form.overwriteConfirm.${replacing}.title`,
				),
				message: this.t(
					`admin.integrations.lettermint.form.overwriteConfirm.${replacing}.message`,
				),
				confirmText: this.t(
					"admin.integrations.lettermint.form.overwriteConfirmButton",
				),
				cancelText: this.t("admin.common.cancel"),
			});
			this.confirming = false;
			if (!ok || !this.isConnected) return;
		}
		this.admitted = true;
		// A blank token omits its KEY entirely — the save contract resolves an
		// absent field against the stored one and rejects an empty string.
		this.dispatchEvent(
			new CustomEvent("save", {
				detail: {
					...(projectToken.length > 0 && { projectToken }),
					...(teamToken.length > 0 && { teamToken }),
					routeId: this.routeId,
				},
				bubbles: true,
				composed: true,
			}),
		);
	}

	override render(): TemplateResult {
		const projectError =
			this.projectTokenTouched && projectTokenPrefixError(this.projectToken);
		const teamClientError =
			this.teamTokenTouched && teamTokenPrefixError(this.teamToken);
		// With a credential stored, either field may stay blank and mean "keep
		// the stored one"; with nothing stored the save is whole-credential,
		// exactly as before.
		const canSubmit =
			(this.hasStoredCredential ||
				(this.projectToken.trim().length > 0 &&
					this.teamToken.trim().length > 0)) &&
			!projectTokenPrefixError(this.projectToken) &&
			!teamTokenPrefixError(this.teamToken);

		return html`
			<div style=${this.actionHeight === null ? "" : `height:${this.actionHeight}px`} class="flex h-full max-h-[85vh] w-md max-w-full flex-col">
				<div class="flex items-center justify-end px-5 pt-5 pb-2">
					<schmancy-icon-button
						icon="close"
						variant="text"
						?disabled=${this.saving}
						aria-label=${this.t("admin.integrations.lettermint.form.closeAriaLabel")}
						@click=${() => this.close()}
					></schmancy-icon-button>
				</div>

				<div ${overflowWithin()} class="flex flex-1 min-h-0 flex-col gap-4 px-5 pb-2">
					<schmancy-typography type="body" token="md">
						${this.t("admin.integrations.lettermint.form.intro")}
					</schmancy-typography>

					<schmancy-input
						label=${this.t("admin.integrations.lettermint.form.projectTokenLabel")}
						.type=${this.projectTokenRevealed ? "text" : "password"}
						?required=${!this.hasStoredCredential}
						autocomplete="off"
						placeholder="lm_…"
						size="md"
						validateOn="touched"
						.value=${this.projectToken}
						?error=${projectError}
						hint=${
							projectError
								? this.t("admin.integrations.lettermint.form.projectTokenError")
								: this.t(
										this.hasStoredCredential
											? "admin.integrations.lettermint.form.projectTokenKeepHint"
											: "admin.integrations.lettermint.form.projectTokenHint",
									)
						}
						@input=${(e: CustomEvent<{ value: string }>) => {
							this.projectToken = e.detail.value;
						}}
						@blur=${() => {
							this.projectTokenTouched = true;
						}}
					>
						<schmancy-icon-button
							slot="actions"
							icon=${this.projectTokenRevealed ? "visibility_off" : "visibility"}
							variant="text"
							aria-label=${
								this.projectTokenRevealed
									? this.t(
											"admin.integrations.lettermint.form.projectTokenHideAriaLabel",
										)
									: this.t(
											"admin.integrations.lettermint.form.projectTokenRevealAriaLabel",
										)
							}
							@click=${() => {
								this.projectTokenRevealed = !this.projectTokenRevealed;
							}}
						></schmancy-icon-button>
					</schmancy-input>

					<schmancy-input
						label=${this.t("admin.integrations.lettermint.form.teamTokenLabel")}
						.type=${this.teamTokenRevealed ? "text" : "password"}
						?required=${!this.hasStoredCredential}
						autocomplete="off"
						placeholder="lm_team_…"
						size="md"
						validateOn="touched"
						.value=${this.teamToken}
						?error=${teamClientError || !!this.teamTokenError}
						hint=${
							this.teamTokenError ??
							(teamClientError
								? this.t("admin.integrations.lettermint.form.teamTokenError")
								: this.t(
										this.hasStoredCredential
											? "admin.integrations.lettermint.form.teamTokenKeepHint"
											: "admin.integrations.lettermint.form.teamTokenHint",
									))
						}
						@input=${(e: CustomEvent<{ value: string }>) => {
							this.teamToken = e.detail.value;
						}}
						@blur=${() => {
							this.teamTokenTouched = true;
						}}
					>
						<schmancy-icon-button
							slot="actions"
							icon=${this.teamTokenRevealed ? "visibility_off" : "visibility"}
							variant="text"
							aria-label=${
								this.teamTokenRevealed
									? this.t(
											"admin.integrations.lettermint.form.teamTokenHideAriaLabel",
										)
									: this.t(
											"admin.integrations.lettermint.form.teamTokenRevealAriaLabel",
										)
							}
							@click=${() => {
								this.teamTokenRevealed = !this.teamTokenRevealed;
							}}
						></schmancy-icon-button>
					</schmancy-input>

					<a
						href=${LETTERMINT_DOCS_URL}
						target="_blank"
						rel="noopener"
						class="inline-flex items-center gap-1 text-sm text-primary-default hover:underline"
					>
						<schmancy-icon size="sm">open_in_new</schmancy-icon>
						${this.t("admin.integrations.lettermint.form.docsLink")}
					</a>

					<div class="flex flex-col gap-2">
						${choose(
							!this.hasStoredCredential
								? "unavailable"
								: this.routesPhase === "idle"
									? "loading"
									: this.routesPhase,
							[
								[
									"unavailable",
									() => html`
										<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
											${this.t("admin.integrations.lettermint.form.route.notYetAvailable")}
										</schmancy-typography>
									`,
								],
								[
									"loading",
									() =>
										html`<schmancy-skeleton shape="text" class="h-14 w-full rounded-xl"></schmancy-skeleton>`,
								],
								[
									"error",
									() => html`
										<div class="flex items-center justify-between gap-2">
											<schmancy-typography type="body" token="sm" class="text-error-default">
												${this.t("admin.integrations.lettermint.form.route.loadError")}
											</schmancy-typography>
											<schmancy-button
												variant="text"
												@click=${() =>
													this.dispatchEvent(
														new CustomEvent("load-routes", {
															bubbles: true,
															composed: true,
														}),
													)}
											>
												${this.t("admin.integrations.lettermint.form.route.retryButton")}
											</schmancy-button>
										</div>
									`,
								],
								[
									"loaded",
									() =>
										this.routes.length === 0
											? html`
													<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
														${this.t("admin.integrations.lettermint.form.route.empty")}
													</schmancy-typography>
												`
											: this.routes.length === 1
												? html``
												: html`
														<schmancy-autocomplete
															label=${this.t("admin.integrations.lettermint.form.route.label")}
															hint=${this.t("admin.integrations.lettermint.form.route.hint")}
															.value=${this.routeId ?? ""}
															@change=${(e: CustomEvent<{ value: string }>) => {
																const next = e.detail.value || null;
																if (next === this.routeId) return;
																this.routeId = next;
															}}
														>
															${repeat(
																this.routes,
																(route) => route.id,
																(route) => html`
																	<schmancy-option
																		value=${route.id}
																		label=${`${route.name} · ${route.routeType}${
																			route.isDefault
																				? ` · ${this.t("admin.integrations.lettermint.form.route.defaultBadge")}`
																				: ""
																		} (${route.projectName})`}
																	></schmancy-option>
																`,
															)}
														</schmancy-autocomplete>
													`,
								],
							],
							() => html``,
						)}
					</div>
				</div>

				<schmancy-surface opacity="translucent" elevated class="sticky bottom-0 flex flex-col gap-2 px-5 py-4 pt-2">
					${when(
						!!this.footerNotice,
						() => html`
							<schmancy-surface type="error" class="flex items-start gap-3 rounded-xl p-4">
								<schmancy-icon class="shrink-0 text-error-on-container">error</schmancy-icon>
								<schmancy-typography type="body" token="sm" class="text-error-on-container">
									${this.footerNotice}
								</schmancy-typography>
							</schmancy-surface>
						`,
					)}
					<div class="flex justify-end">
						<schmancy-button
							variant="filled"
							color="primary"
							.busy=${this.saving || this.confirming || this.admitted}
                            ?disabled=${!canSubmit}
							@click=${() => this.save()}
						>
							${this.t("admin.integrations.lettermint.form.saveButton")}
						</schmancy-button>
					</div>
				</schmancy-surface>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"lettermint-credentials-form": LettermintCredentialsForm;
	}
}
